import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { interval, Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import { MatDialog } from '@angular/material/dialog';

interface TokenPayload {
  sub: string;
  name: string;
  jobs: string[];
  job_names: string[];
  email: string;
  created_at: string;
  is_active: boolean;
  exp: number;
}

/**
 * Automatikus kijelentkezés és token refresh service
 * - Kezeli az inaktivitás figyelését
 * - Silent refresh: 15 perccel lejárat előtt automatikusan megújítja a tokent ha aktív a felhasználó
 * - Automatikus kijelentkezés csak inaktivitás esetén
 */
@Injectable({
  providedIn: 'root'
})
export class AutoLogoutService {
  private readonly INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 60 perc milliszekundumban
  private readonly CHECK_INTERVAL = 10 * 1000; // 10 másodpercenként ellenőrzés
  private readonly REFRESH_BEFORE_EXPIRY = 15 * 60 * 1000; // 15 perc lejárat előtt refresh
  private readonly ACTIVITY_TOKEN_CHECK_THROTTLE = 5 * 1000; // Aktivitás alatti token ellenőrzés throttle
  private lastTokenCheckTime: number = 0;
  
  private lastActivityTime: number = Date.now();
  private checkSubscription: Subscription | null = null;
  private isEnabled: boolean = true;
  private isMonitoring: boolean = false;

  constructor(
    private router: Router,
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object,
    private dialog: MatDialog
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.setupActivityListeners();
    }
  }

  /**
   * Felhasználói aktivitás figyelők beállítása
   */
  private setupActivityListeners(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // Összes olyan esemény, ami felhasználói aktivitást jelent
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, () => this.resetTimer(), true);
    });
  }

  /**
   * Időzítő újraindítása (aktivitás észlelve)
   * Aktivitáskor a token érvényességét is ellenőrizzük (throttled)
   */
  private resetTimer(): void {
    this.lastActivityTime = Date.now();

    // Throttled token érvényesség ellenőrzés aktivitáskor
    const now = Date.now();
    if (this.isMonitoring && now - this.lastTokenCheckTime >= this.ACTIVITY_TOKEN_CHECK_THROTTLE) {
      this.lastTokenCheckTime = now;
      this.enforceValidToken();
    }
  }

  /**
   * Token érvényességének azonnali ellenőrzése.
   * Ha nincs token vagy lejárt, azonnal kijelentkezés.
   * @returns true ha a token érvényes, false ha kijelentkezés történt
   */
  private enforceValidToken(): boolean {
    if (!isPlatformBrowser(this.platformId)) return true;

    const token = localStorage.getItem('token');
    if (!token) {
      console.log('Nincs érvényes token - azonnali kijelentkezés');
      this.logout('expired');
      return false;
    }

    const payload = this.decodeToken(token);
    if (!payload || !payload.exp) {
      console.log('Érvénytelen token payload - azonnali kijelentkezés');
      this.logout('expired');
      return false;
    }

    const nowSec = Math.floor(Date.now() / 1000);
    if (payload.exp <= nowSec) {
      console.log('Token lejárt - azonnali kijelentkezés');
      this.logout('expired');
      return false;
    }

    return true;
  }

  /**
   * Automatikus kijelentkezés figyelés indítása
   */
  startMonitoring(enabled: boolean = true): void {
    this.isEnabled = enabled;
    
    if (!this.isEnabled) {
      this.stopMonitoring();
      return;
    }

    if (this.isMonitoring) {
      return; // Már fut
    }

    this.isMonitoring = true;
    this.lastActivityTime = Date.now();

    // Azonnali token érvényesség ellenőrzés indításkor
    if (!this.enforceValidToken()) return;

    // Időszakos ellenőrzés indítása
    this.checkSubscription = interval(this.CHECK_INTERVAL).subscribe(() => {
      this.checkInactivity();
    });
  }

  /**
   * Figyelés leállítása
   */
  stopMonitoring(): void {
    if (this.checkSubscription) {
      this.checkSubscription.unsubscribe();
      this.checkSubscription = null;
    }
    this.isMonitoring = false;
  }

  /**
   * Inaktivitás ellenőrzése és token refresh kezelés
   */
  private checkInactivity(): void {
    if (!this.isEnabled) return;

    // 1. Token érvényesség - ha lejárt vagy nincs, azonnal kijelentkezés
    if (!this.enforceValidToken()) return;

    const now = Date.now();
    const inactiveTime = now - this.lastActivityTime;

    // 2. Token silent refresh, ha közel a lejárat
    this.checkTokenExpiry();

    // 3. Inaktivitás ellenőrzés
    if (inactiveTime >= this.INACTIVITY_TIMEOUT) {
      console.log('60 perc inaktivitás - automatikus kijelentkezés');
      this.logout('inactivity');
    }
  }

  /**
   * Token lejárat ellenőrzés és silent refresh
   */
  private checkTokenExpiry(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const payload = this.decodeToken(token);
      if (!payload || !payload.exp) return;

      const now = Math.floor(Date.now() / 1000); // Másodpercben
      const expiresIn = payload.exp - now;
      const expiresInMs = expiresIn * 1000;

      // Ha 15 percen belül lejár és a felhasználó aktív volt az utóbbi 5 percben
      const recentlyActive = (Date.now() - this.lastActivityTime) < (5 * 60 * 1000);
      
      if (expiresInMs > 0 && expiresInMs <= this.REFRESH_BEFORE_EXPIRY && recentlyActive) {
        console.log(`Token ${Math.floor(expiresInMs / 60000)} perc múlva lejár, silent refresh...`);
        this.refreshToken();
      }
    } catch (error) {
      console.error('Hiba a token ellenőrzésekor:', error);
    }
  }

  /**
   * Token dekódolása
   */
  private decodeToken(token: string): TokenPayload | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload;
    } catch (error) {
      console.error('Token dekódolási hiba:', error);
      return null;
    }
  }

  /**
   * Token megújítása (silent refresh)
   */
  private refreshToken(): void {
    const apiUrl = environment.apiUrl || 'http://localhost:8000';
    const token = localStorage.getItem('token');
    
    if (!token) return;

    this.http.post<{ access_token: string }>(`${apiUrl}/auth/refresh`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    }).subscribe({
      next: (response) => {
        localStorage.setItem('token', response.access_token);
        console.log('✅ Token sikeresen megújítva (silent refresh)');
        
        // Aktivitás idő frissítése hogy ne jelentkeztessen ki
        this.lastActivityTime = Date.now();
      },
      error: (error) => {
        console.error('❌ Token refresh hiba:', error);
        // Ha a refresh sikertelen, kijelentkeztetjük
        if (error.status === 401) {
          this.logout('expired');
        }
      }
    });
  }

  /**
   * Automatikus kijelentkezés végrehajtása
   */
  private logout(reason: 'inactivity' | 'expired' = 'inactivity'): void {
    this.stopMonitoring();
    this.dialog.closeAll();
    
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('token');
      localStorage.removeItem('userData');
    }
    
    const message = reason === 'expired'
      ? 'A munkamenet lejárt, kérlek jelentkezz be újra.'
      : 'Automatikusan kijelentkeztetésre került 60 perc inaktivitás miatt.';

    this.router.navigate(['/login'], { 
      queryParams: { 
        reason,
        message
      } 
    });
  }

  /**
   * Beállítás frissítése
   */
  updateSettings(enabled: boolean): void {
    this.isEnabled = enabled;
    
    if (enabled && !this.isMonitoring) {
      this.startMonitoring(true);
    } else if (!enabled && this.isMonitoring) {
      this.stopMonitoring();
    }
  }

  /**
   * Service megszüntetése
   */
  ngOnDestroy(): void {
    this.stopMonitoring();
  }
}
